<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { ref } from "vue"

import FlashAlerts from "@/components/FlashAlerts.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { formatDate } from "@/lib/format"
import { ICON } from "@/lib/icons"
import { accountInvitationPath, accountInvitationsPath } from "@/routes"

interface InvitationEntry {
  id: string
  token: string
  active: boolean
  expired: boolean
  max_uses: number
  redeemed_count: number
  expires_at: string
  created_at: string
  invite_url: string
}

const props = defineProps<{
  invitations: InvitationEntry[]
}>()

const { title } = useNavbar({ title: "Invitations" })

const generating = ref(false)
const copiedId = ref<string | null>(null)

function generate() {
  generating.value = true
  router.post(
    accountInvitationsPath(),
    {},
    {
      onFinish: () => (generating.value = false),
    },
  )
}

function revoke(id: string) {
  router.delete(accountInvitationPath({ id }))
}

async function copyLink(invitation: InvitationEntry) {
  await navigator.clipboard.writeText(invitation.invite_url)
  copiedId.value = invitation.id
  setTimeout(() => (copiedId.value = null), 2000)
}

function statusLabel(inv: InvitationEntry): string {
  if (inv.expired) return "Expired"
  if (inv.redeemed_count >= inv.max_uses) return "Used"
  return "Active"
}

function statusColor(inv: InvitationEntry): "success" | "neutral" {
  if (inv.expired || inv.redeemed_count >= inv.max_uses) return "neutral"
  return "success"
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <FlashAlerts />

    <div class="flex items-center justify-between">
      <span class="text-muted text-sm">
        {{ invitations.length }} {{ invitations.length === 1 ? "invitation" : "invitations" }}
      </span>
      <UButton
        :icon="ICON.add"
        label="Generate"
        color="neutral"
        variant="subtle"
        size="sm"
        :loading="generating"
        @click="generate"
      />
    </div>

    <SbList v-if="invitations.length" bordered separator>
      <SbListItem v-for="inv in invitations" :key="inv.id">
        <SbListSection>
          <SbListLabel :lines="1" class="font-mono text-xs">
            {{ inv.token.slice(0, 16) }}...
          </SbListLabel>
          <SbListLabel caption>
            Created {{ formatDate(inv.created_at) }} · Expires {{ formatDate(inv.expires_at) }} ·
            {{ inv.redeemed_count }}/{{ inv.max_uses }} used
          </SbListLabel>
        </SbListSection>
        <SbListSection side>
          <div class="flex items-center gap-2">
            <UBadge :color="statusColor(inv)" variant="subtle" size="sm">
              {{ statusLabel(inv) }}
            </UBadge>
            <UButton
              v-if="inv.active"
              :icon="copiedId === inv.id ? ICON.check : ICON.duplicate"
              :label="copiedId === inv.id ? 'Copied' : 'Copy'"
              color="neutral"
              variant="ghost"
              size="xs"
              @click="copyLink(inv)"
            />
            <UButton
              :icon="ICON.delete"
              color="neutral"
              variant="ghost"
              size="xs"
              square
              @click="revoke(inv.id)"
            />
          </div>
        </SbListSection>
      </SbListItem>
    </SbList>
  </NarrowPage>
</template>
